// メニューグループとアイテムの定義
// path を権限の一意なキーとして使用します

export const BOOKING_MANAGER_ROLE = "booking_manager";
export const BOOKING_SETTINGS_PATH = "/settings/booking";

export const getDefaultAuthenticatedPath = (user) => (
    user?.role === BOOKING_MANAGER_ROLE ? BOOKING_SETTINGS_PATH : "/mypage"
);

export const shouldSkipSubscriptionCheck = (user) => user?.role === BOOKING_MANAGER_ROLE;

export const MENU_GROUPS = [
  {
    title: "📊 ダッシュボード",
    path: "/mypage",
    type: "single",
    roles: ["admin", "client_admin", "staff", "admin_staff", "metatest"],
    id: "dashboard"
  },
  {
    title: "📝 準備・設定",
    type: "group",
    id: "preparation",
    items: [
      { label: "🛑 会社概要 & NG設定", path: "/settings", roles: ["admin", "client_admin", "admin_staff", "staff"] },
      { label: "🧩 文面テンプレート", path: "/templates", roles: ["admin", "client_admin", "admin_staff", "staff"] },
      { label: "🚀 リストダウンロード", path: "/list-download", roles: ["admin", "client_admin", "staff", "admin_staff"] },
      { label: "📂 マイリスト管理", path: "/client-sales-lists", roles: ["admin", "client_admin", "admin_staff", "staff"] },
      { label: "📋 送付先リスト", path: "/list-templates", roles: ["admin", "client_admin", "admin_staff", "staff"] },
      { label: "📅 日程調整", path: BOOKING_SETTINGS_PATH, roles: ["admin", "admin_staff", "client_admin", BOOKING_MANAGER_ROLE] },
    ]
  },
  {
    title: "📤 送信アプローチ",
    type: "group",
    id: "sending",
    items: [
      { label: "📤 送信", path: "/send", roles: ["admin", "client_admin", "admin_staff", "staff"] },
      // { label: "🚀 拡張機能 送信", path: "/extension", roles: ["admin", "client_admin", "staff", "admin_staff"] }, // ★ ここをコメントアウト
      { label: "📋 送信履歴", path: "/history", roles: ["admin", "client_admin", "staff", "admin_staff"] },
    ]
  },
  {
    title: "🤝 案件・顧客管理",
    type: "group",
    id: "management",
    items: [
      { label: "📦 案件管理", path: "/deals", roles: ["admin", "client_admin", "staff", "admin_staff", "metatest"] },
      { label: "✅ 受注一覧", path: "/orders", roles: ["admin", "client_admin", "staff", "admin_staff", "metatest"] },
      { label: "🏢 顧客台帳 (CRM)", path: "/customer-ledger", roles: ["admin", "client_admin", "staff", "admin_staff", "metatest"] },
      { label: "🕵️‍♀️ クリックログ", path: "/click-logs", roles: ["admin", "client_admin", "staff", "admin_staff"] },
      { label: "📈 売上分析", path: "/sales-analytics", roles: ["admin", "client_admin", "staff", "admin_staff"] },
    ]
  },
  {
    title: "📢 マーケティング",
    type: "group",
    id: "marketing",
    items: [
      { label: "📥 リード管理", path: "/marketing/leads", roles: ["admin", "client_admin", "staff", "admin_staff", "metatest"] },
      { label: "📧 メール配信", path: "/marketing/email", roles: ["admin", "client_admin", "admin_staff", "staff"] }, 
      { label: "🤖 シナリオ配信", path: "/marketing/scenarios", roles: ["admin", "client_admin", "admin_staff", "staff"] }, 
      { label: "📝 フォーム作成", path: "/marketing/forms", roles: ["admin", "client_admin", "admin_staff", "staff"] },
      { label: "🌐 LP制作・公開", path: "/marketing/lps", roles: ["admin", "client_admin", "admin_staff", "staff"] },
      { label: "📄 資料作成", path: "/marketing/presentations", roles: ["admin", "client_admin", "admin_staff", "staff"] },
      { label: "📈 マーケティング設定", path: "/marketing/tracking", roles: ["admin", "client_admin", "admin_staff", "staff", "metatest"] }, 
    ]
  },
  {
    title: "⚙️ アカウント・管理",
    type: "group",
    id: "account",
    items: [
      { label: "🔑 アカウント管理", path: "/account", roles: ["admin", "client_admin", "admin_staff", "staff"] },
      { label: "🧑‍💼 ユーザー管理", path: "/users", roles: ["admin", "client_admin", "admin_staff", "staff"] },
      { label: "🧾 利用明細", path: "/invoices", roles: ["admin", "client_admin", "admin_staff", "staff"] },
      { label: "❓ ヘルプ & サポート", path: "/help", roles: ["admin", "client_admin", "staff", "admin_staff"] },
    ]
  },
  {
    title: "🔑 APIキー",
    type: "group",
    id: "api_keys",
    items: [
      {
        label: "🔐 APIキー / AI連携",
        path: "/api-keys",
        roles: ["admin", "admin_staff", "client_admin", "staff"],
        visibility: "api_keys_surface"
      },
      {
        label: "🔐 APIキー / AI連携",
        path: "/oem/api-keys",
        roles: ["admin", "admin_staff", "client_admin", "staff"],
        visibility: "oem_api_keys_surface"
      },
    ]
  }
];

export const OEM_RESTRICTED_PATH_PREFIXES = ["/extension", "/account", "/invoices", "/help", "/agency"];
export const OEM_BILLING_PATH_PREFIXES = ["/plan-selection", "/payment-success"];

const matchesPathPrefix = (path, prefixes) => {
    if (!path) return false;
    const normalizedPath = path === "/app" ? "/" : path.replace(/^\/app(?=\/)/, "");
    return prefixes.some(prefix => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`));
};

export const isOemRestrictedPath = (path) => matchesPathPrefix(path, OEM_RESTRICTED_PATH_PREFIXES);
export const isOemBillingPath = (path) => matchesPathPrefix(path, OEM_BILLING_PATH_PREFIXES);

// 全ての権限パス（URL）のリストを取得するヘルパー関数
export const getAllPermissionPaths = () => {
    let paths = [];
    MENU_GROUPS.forEach(group => {
        if (group.type === 'single') {
            paths.push(group.path);
        } else if (group.type === 'group') {
            group.items.forEach(item => {
                paths.push(item.path);
            });
        }
    });
    return paths;
};

// ユーザーが特定のパスにアクセス権限を持っているかチェックする関数
export const hasPermission = (user, path) => {
    // 1. ユーザー情報がない場合はNG
    if (!user) return false;

    if (user.oem_id && isOemRestrictedPath(path)) {
        return false; 
    }

    // 日程調整専用ロールは、日程調整画面以外をすべて拒否する。
    // /app 付きで渡される場合も同じパスとして扱う。
    if (user.role === BOOKING_MANAGER_ROLE) {
        const normalizedPath = String(path || "").replace(/^\/app(?=\/)/, "");
        return normalizedPath === BOOKING_SETTINGS_PATH;
    }

    // ★★★ 修正: ダッシュボード(/mypage)は、権限設定に関わらず全ユーザーに許可する ★★★
    // これがないと、ログイン直後に「権限なし」と判定されてログイン画面に戻されるループに陥る可能性があります
    if (path === "/mypage" || path === "/app/mypage") {
        return true;
    }

    // 2. 管理者系 (admin, client_admin, admin_staff) は常に許可
    // ※ admin_staff も基本全権限だが、もし制限したい場合はここを調整
    if (['admin', 'client_admin', 'admin_staff'].includes(user.role)) {
        return true;
    }

    // ★★★ ここに metatest 専用の「ホワイトリスト」ロジックを差し込みます ★★★
    if (user.role === 'metatest') {
        const allowedPaths = [
            "/mypage",
            "/app/mypage",
            "/marketing/leads",
            "/marketing/tracking",
            "/marketing/meta-dashboard",
            "/deals",           // ★追加
            "/orders",          // ★追加
            "/customer-ledger"
        ];
        // 指定したパス以外は全て拒否
        return allowedPaths.includes(path);
    }

    // 3. staff の場合のみ権限チェック
    if (user.role === 'staff') {
        // permissions がない、または配列でない場合はNG
        if (!user.permissions || !Array.isArray(user.permissions)) {
            return false;
        }
        // パスが許可リストに含まれているか
        // 完全一致だけでなく、サブパス（例: /deals/details/1 -> /deals）も許可する場合はロジック調整が必要
        // ここでは単純化のため、メニュー定義にあるパスとの完全一致または前方一致で判定
        return user.permissions.some(allowedPath => path === allowedPath || path.startsWith(allowedPath + '/'));
    }

    return false;
};
